import DatePicker from "@hassanmojab/react-modern-calendar-datepicker";
import {
  Card,
  Grid,
  Icon,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Divider,
  Chip,
} from "@mui/material";

import { DataGrid, faIR } from "@mui/x-data-grid";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList   
} from "recharts";

import Loading from "components/Loading";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import queryString from "components/queryString";

import moment from "jalali-moment";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getRequestGroup,
  getReservationNotebookStats,
  getPatientsDailyCount,
  getTransactionsDailyByCenter,
} from "api/routes";

/* ---------------- helpers ---------------- */

function jDateToInt(d) {
  if (!d?.year || !d?.month || !d?.day) return null;
  return Number(`${d.year}${String(d.month).padStart(2, "0")}${String(d.day).padStart(2, "0")}`);
}

function intToJDate(intDate) {
  const s = String(intDate || "");
  if (s.length !== 8) return "";
  return `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`;
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function formatFa(n) {
  return safeNum(n).toLocaleString("fa-IR");
}

/* ---------------- merge ---------------- */

function mergeByDate({ notebookDays = [], patientsDays = [], txDays = [] }) {
  const map = new Map();

  // Notebook: {reservationDate,totalSlots,filledSlots,emptySlots,fillPercent}
  for (const r of notebookDays || []) {
    const d = Number(r.reservationDate);
    if (!d) continue;
    map.set(d, {
      id: d,
      reservationDate: d,
      notebook_totalSlots: safeNum(r.totalSlots),
      notebook_filledSlots: safeNum(r.filledSlots),
      notebook_emptySlots: safeNum(r.emptySlots),
      notebook_fillPercent: safeNum(r.fillPercent),
      patientsCount: 0,
      txCount: 0,
      txSuccessCount: 0,
      txSuccessTotalPrice: 0,
    });
  }

  // Patients: {reservationDate, patientsCount}
  for (const r of patientsDays || []) {
    const d = Number(r.reservationDate);
    if (!d) continue;
    const row = map.get(d) || {
      id: d,
      reservationDate: d,
      notebook_totalSlots: 0,
      notebook_filledSlots: 0,
      notebook_emptySlots: 0,
      notebook_fillPercent: 0,
      patientsCount: 0,
      txCount: 0,
      txSuccessCount: 0,
      txSuccessTotalPrice: 0,
    };
    row.patientsCount = safeNum(r.patientsCount);
    map.set(d, row);
  }

  // Transactions: {trDate, transactionsCount, successCount, failCount, successTotalPrice}
  for (const r of txDays || []) {
    const d = Number(r.trDate);
    if (!d) continue;
    const row = map.get(d) || {
      id: d,
      reservationDate: d,
      notebook_totalSlots: 0,
      notebook_filledSlots: 0,
      notebook_emptySlots: 0,
      notebook_fillPercent: 0,
      patientsCount: 0,
      txCount: 0,
      txSuccessCount: 0,
      txSuccessTotalPrice: 0,
    };
    row.txCount = safeNum(r.transactionsCount);
    row.txSuccessCount = safeNum(r.successCount);
    row.txSuccessTotalPrice = safeNum(r.successTotalPrice);
    map.set(d, row);
  }

  // پیش‌فرض نزولی (ولی داخل DataGrid هم sortModel گذاشتیم)
  return Array.from(map.values()).sort((a, b) => b.reservationDate - a.reservationDate);
}

/* ---------------- ui: summary card ---------------- */

function SummaryCard({ title, items }) {
  return (
    <Card style={{ padding: "1rem", borderRadius: 16 }}>
      <MDTypography variant="h6" style={{ marginBottom: 8 }}>
        {title}
      </MDTypography>
      <Divider />
      <Grid container spacing={2} style={{ marginTop: 8 }}>
        {items.map((it) => (
          <Grid item xs={12} sm={6} md={3} key={it.label}>
            <MDBox
              style={{
                border: "1px solid rgba(0,0,0,0.06)",
                borderRadius: 14,
                padding: "12px 14px",
                background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
              }}
            >
              <MDTypography variant="button" color="text">
                {it.label}
              </MDTypography>
              <MDTypography variant="h6" style={{ marginTop: 6, fontWeight: 800 }}>
                {it.value}
              </MDTypography>
            </MDBox>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
}

/* ---------------- chart helpers ---------------- */

/**
 * پالت حرفه‌ای‌تر:
 * - رنگ‌ها کمتر “جیغ” و بیشتر داشبوردی
 * - هارمونی آبی/سبز/کهربایی/بنفش/فیروزه‌ای/رز
 */
const COLORS = {
  total: "#2563EB",     // blue-600
  filled: "#16A34A",    // green-600
  empty: "#F59E0B",     // amber-500
  patients: "#7C3AED",  // violet-600
  txCount: "#0891B2",   // cyan-600
  txSuccess: "#1D4ED8", // blue-700
  amount: "#DB2777",    // pink-600
};

function ChartCard({ title, subtitle, children }) {
  return (
    <Card style={{ padding: "1rem", borderRadius: 16 }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <div>
          <MDTypography variant="h6" style={{ fontWeight: 800 }}>
            {title}
          </MDTypography>
          {subtitle ? (
            <MDTypography variant="button" color="text" style={{ opacity: 0.7 }}>
              {subtitle}
            </MDTypography>
          ) : null}
        </div>
        <Chip
          size="small"
          label="Daily"
          style={{
            borderRadius: 10,
            fontWeight: 800,
            background: "linear-gradient(90deg, rgba(37,99,235,0.12) 0%, rgba(124,58,237,0.10) 100%)",
          }}
        />
      </MDBox>
      <Divider style={{ margin: "10px 0 18px" }} />
      {children}
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.96)",
        border: "1px solid rgba(2, 6, 23, 0.10)",
        borderRadius: 14,
        padding: "10px 12px",
        boxShadow: "0 14px 40px rgba(2, 6, 23, 0.18)",
        direction: "rtl",
        minWidth: 200,
        backdropFilter: "blur(6px)",
        fontSize:14
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 8, color: "#0f172a" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 6 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 99,
                background: p.color,
                boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
              }}
            />
            <span style={{ color: "#0f172a", fontWeight: 800 }}>{p.name}</span>
          </span>

          <span style={{ fontWeight: 900, color: "#0f172a" }}>
            {p.name.includes("مبلغ") ? `${formatFa(p.value)} میلیون` : formatFa(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- main page ---------------- */

export default function ClinicAnalytics() {
  const [loading, setLoading] = useState(false);

  const [selectedRange, setSelectedRange] = useState(() => {
    const now = moment().locale("fa");
    const end = {
      day: parseInt(now.format("D")),
      month: parseInt(now.format("M")),
      year: parseInt(now.format("YYYY")),
    };
    const start = {
      day: parseInt(now.clone().subtract(29, "days").format("D")),
      month: parseInt(now.clone().subtract(29, "days").format("M")),
      year: parseInt(now.clone().subtract(29, "days").format("YYYY")),
    };
    return { from: start, to: end };
  });

  const [requestGroupData, setRequestGroupData] = useState([]);
  const [clickedRequestGroupId, setClickedRequestGroupId] = useState(13);
  const [isNoteBook, setIsNoteBook] = useState(true);

  const [notebookDays, setNotebookDays] = useState([]);
  const [patientsDays, setPatientsDays] = useState([]);
  const [txDays, setTxDays] = useState([]);

  const [notebookSummary, setNotebookSummary] = useState({
    totalSlots: 0,
    filledSlots: 0,
    emptySlots: 0,
    fillPercent: 0,
  });

  useEffect(() => {
    getRequestGroup(
      (isOk, data) => {
        if (!isOk) return toast.error(data.message);
        setRequestGroupData(data || []);
      },
      queryString({ all: 1 })
    );
  }, []);

  const fromInt = useMemo(() => jDateToInt(selectedRange?.from), [selectedRange]);
  const toInt = useMemo(() => jDateToInt(selectedRange?.to), [selectedRange]);

  const mergedRows = useMemo(
    () => mergeByDate({ notebookDays, patientsDays, txDays }),
    [notebookDays, patientsDays, txDays]
  );

  const chartData = useMemo(() => {
    // چارت صعودی بهتره
    const asc = [...mergedRows].sort((a, b) => a.reservationDate - b.reservationDate);
    return asc.map((r) => ({
      date: intToJDate(r.reservationDate),
      totalSlots: safeNum(r.notebook_totalSlots),
      filledSlots: safeNum(r.notebook_filledSlots),
      emptySlots: safeNum(r.notebook_emptySlots),
      patients: safeNum(r.patientsCount),
      txCount: safeNum(r.txCount),
      txSuccess: safeNum(r.txSuccessCount),
      successAmountM: safeNum(r.txSuccessTotalPrice) / 1_000_000,
    }));
  }, [mergedRows]);

  const summaryItems = useMemo(() => {
    const patientsTotal = (patientsDays || []).reduce((s, r) => s + safeNum(r.patientsCount), 0);
    const txSuccessCountTotal = (txDays || []).reduce((s, r) => s + safeNum(r.successCount), 0);
    const txSuccessSumTotal = (txDays || []).reduce((s, r) => s + safeNum(r.successTotalPrice), 0);

    return [
      { label: "کل ظرفیت دفتر", value: formatFa(notebookSummary.totalSlots) },
      { label: "کل پر شده دفتر", value: formatFa(notebookSummary.filledSlots) },
      { label: "کل خالی دفتر", value: formatFa(notebookSummary.emptySlots) },
      { label: "درصد پرشدگی", value: `${formatFa(notebookSummary.fillPercent)}%` },
      { label: "کل بیماران", value: formatFa(patientsTotal) },
      { label: "تعداد تراکنش موفق", value: formatFa(txSuccessCountTotal) },
      { label: "جمع مبلغ موفق", value: formatFa(txSuccessSumTotal) },
    ];
  }, [patientsDays, txDays, notebookSummary]);

  const renderRangeInput = ({ ref }) => {
    const fromText = selectedRange?.from
      ? `${selectedRange.from.year}/${selectedRange.from.month}/${selectedRange.from.day}`
      : "----/--/--";
    const toText = selectedRange?.to
      ? `${selectedRange.to.year}/${selectedRange.to.month}/${selectedRange.to.day}`
      : "----/--/--";

    return (
      <div ref={ref} style={{ position: "relative" }}>
        <input
          readOnly
          placeholder="بازه تاریخ"
          value={`${fromText}  تا  ${toText}`}
          style={{
            outline: "none",
            textAlign: "center",
            border: "1px solid rgba(37,99,235,0.55)",
            borderRadius: 100,
            color: "#0f172a",
            cursor: "pointer",
            height: "44.3px",
            boxShadow: "0 0.7rem 1.2rem rgba(37, 99, 235, 0.18)",
            width: "260px",
            fontFamily: "IRANSansX",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          }}
        />
      </div>
    );
  };

  const loadAnalytics = () => {
    if (!fromInt || !toInt) return toast.error("لطفاً بازه تاریخ را کامل انتخاب کن.");

    setLoading(true);
    let pending = 3;
    const done = () => {
      pending -= 1;
      if (pending <= 0) setLoading(false);
    };

    const commonQuery = {
      from: fromInt,
      to: toInt,
      requestGroupId: clickedRequestGroupId,
      ...(isNoteBook ? { isNoteBook: 1 } : {}),
    };

    getReservationNotebookStats(queryString(commonQuery), (ok, data) => {
      if (!ok) toast.error(data.message);
      else {
        setNotebookSummary(
          data?.summary || { totalSlots: 0, filledSlots: 0, emptySlots: 0, fillPercent: 0 }
        );
        setNotebookDays(data?.days || []);
      }
      done();
    });

    getPatientsDailyCount(queryString({ from: fromInt, to: toInt }), (ok, data) => {
      if (!ok) toast.error(data.message);
      else setPatientsDays(Array.isArray(data) ? data : data?.daily || []);
      done();
    });

    getTransactionsDailyByCenter(queryString({ from: String(fromInt), to: String(toInt) }), (ok, data) => {
      if (!ok) toast.error(data.message);
      else setTxDays(Array.isArray(data) ? data : data?.daily || []);
      done();
    });
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- DataGrid ---------------- */

  const columns = useMemo(
    () => [
      {
        field: "reservationDate",
        headerName: "تاریخ",
        flex: 1,
        minWidth: 120,
        valueGetter: (p) => intToJDate(p.value),
      },
      {
        field: "notebook_totalSlots",
        headerName: "ظرفیت دفتر",
        flex: 1,
        minWidth: 120,
        valueFormatter: (p) => formatFa(p.value),
      },
      {
        field: "notebook_filledSlots",
        headerName: "پر شده",
        flex: 1,
        minWidth: 110,
        valueFormatter: (p) => formatFa(p.value),
      },
      {
        field: "notebook_emptySlots",
        headerName: "خالی",
        flex: 1,
        minWidth: 110,
        valueFormatter: (p) => formatFa(p.value),
      },
      {
        field: "notebook_fillPercent",
        headerName: "٪ پرشدگی",
        flex: 1,
        minWidth: 110,
        valueFormatter: (p) => `${formatFa(p.value)}%`,
      },
      {
        field: "patientsCount",
        headerName: "بیماران",
        flex: 1,
        minWidth: 140,
        valueFormatter: (p) => formatFa(p.value),
      },
      {
        field: "txCount",
        headerName: "تراکنش‌ها",
        flex: 1,
        minWidth: 110,
        valueFormatter: (p) => formatFa(p.value),
      },
      {
        field: "txSuccessCount",
        headerName: "موفق",
        flex: 1,
        minWidth: 90,
        valueFormatter: (p) => formatFa(p.value),
      },
      {
        field: "txSuccessTotalPrice",
        headerName: "جمع مبلغ موفق",
        flex: 1.2,
        minWidth: 150,
        valueFormatter: (p) => formatFa(p.value),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <Loading loading={loading} />

      <MDBox pt={2}>
        <Grid container spacing={3}>
          {/* ---------------- Header / Filters ---------------- */}
          <Grid item xs={12}>
            <Card style={{ borderRadius: 16 }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <MDTypography variant="h6" color="white" style={{ fontWeight: 900 }}>
                  صفحه آنالیز
                </MDTypography>
                <DashboardNavbar />
              </MDBox>

              <MDBox px={2} pt={3} pb={2} display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                <DatePicker
                  value={selectedRange}
                  onChange={setSelectedRange}
                  renderInput={renderRangeInput}
                  shouldHighlightWeekends
                  locale="fa"
                  calendarPopperPosition="bottom"
                  colorPrimary="#2563EB"
                  colorPrimaryLight="rgba(37, 99, 235, 0.25)"
                />

                <MDButton
                  variant="gradient"
                  color="info"
                  style={{ borderRadius: 16, padding: "0 18px", height: 42 }}
                  onClick={loadAnalytics}
                >
                  اعمال فیلتر
                </MDButton>

                <MDBox ml="auto" display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small" style={{ color: "rgba(0,0,0,0.6)" }}>
                    event
                  </Icon>
                  <MDTypography variant="button" color="text">
                    از {intToJDate(fromInt)} تا {intToJDate(toInt)}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox px={2} pb={2}>
                <RadioGroup
                  row
                  value={clickedRequestGroupId}
                  style={{ marginRight: 10 }}
                  onChange={(event) => setClickedRequestGroupId(Number(event.target.value))}
                >
                  {requestGroupData
                    .filter((x) => (isNoteBook ? !!x.isNoteBook : true))
                    .map((item) => (
                      <FormControlLabel
                        key={item.requestGroupId}
                        control={<Radio size="small" />}
                        value={item.requestGroupId}
                        label={item.nameGroup}
                      />
                    ))}
                </RadioGroup>
              </MDBox>
            </Card>
          </Grid>

          {/* ---------------- Summary ---------------- */}
          <Grid item xs={12}>
            <SummaryCard title="خلاصه بازه" items={summaryItems} />
          </Grid>

         

          <Grid item xs={12} md={6}>
            <ChartCard title="نوبت دهی" subtitle="تعداد بیماران روزانه">
              {chartData.length === 0 ? (
                <MDTypography variant="button">داده‌ای برای نمایش وجود ندارد.</MDTypography>
              ) : (
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={chartData} barCategoryGap={14} barGap={6}>
                    <defs>
                      <linearGradient id="gPatients" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.patients} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={COLORS.patients} stopOpacity={0.18} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(2, 6, 23, 0.08)" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} axisLine={{ stroke: "rgba(2,6,23,0.20)" }} />
                    <YAxis tick={{ fontSize: 12 }} axisLine={{ stroke: "rgba(2,6,23,0.20)" }} />
                    <Tooltip content={<CustomTooltip />} />
<Legend 
      iconType="circle"
      wrapperStyle={{
        fontSize: 14,
        fontWeight: 800,
      }}
    />
                    <Bar
                      dataKey="patients"
                      name="تعداد"
                      fill="url(#gPatients)"
                      radius={[12, 12, 0, 0]}
                      barSize={26}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="چارت تراکنش‌ها" subtitle="تعداد + مبلغ موفق (میلیون)">
              {chartData.length === 0 ? (
                <MDTypography variant="button">داده‌ای برای نمایش وجود ندارد.</MDTypography>
              ) : (
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(2, 6, 23, 0.08)" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} axisLine={{ stroke: "rgba(2,6,23,0.20)" }} />
                    <YAxis tick={{ fontSize: 12 }} axisLine={{ stroke: "rgba(2,6,23,0.20)" }} />
                    <Tooltip content={<CustomTooltip />} />
           <Legend
      iconType="circle"
      wrapperStyle={{
        fontSize: 14,
        fontWeight: 800,
      }}
    />

                    <Line
                      type="monotone"
                      dataKey="txCount"
                      name="تعداد تراکنش"
                      stroke={COLORS.txCount}
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="txSuccess"
                      name="موفق"
                      stroke={COLORS.txSuccess}
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="successAmountM"
                      name="جمع مبلغ موفق (میلیون)"
                      stroke={COLORS.amount}
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          {/* ---------------- DataGrid table (Daily) ---------------- */}
          <Grid item xs={12}>
            <Card style={{ padding: "1rem", borderRadius: 16 }}>
              <MDTypography variant="h6" style={{ marginBottom: 8, fontWeight: 900 }}>
                خروجی روزانه
              </MDTypography>
              <Divider style={{ marginBottom: 12 }} />

              <div style={{ height: 560, width: "100%" }}>
                <DataGrid
                  rows={mergedRows}
                  columns={columns}
                  localeText={faIR.components.MuiDataGrid.defaultProps.localeText}
                  pageSize={10}
                  rowsPerPageOptions={[10, 20, 50]}
                  disableSelectionOnClick
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "reservationDate", sort: "desc" }],
                    },
                  }}
                  sx={{
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.06)",
                    "& .MuiDataGrid-columnHeaders": {
                      background: "linear-gradient(90deg, #f8fafc 0%, #eef2ff 60%, #ecfeff 100%)",
                      fontWeight: 900,
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "rgba(2,6,23,0.02)",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "rgba(37, 99, 235, 0.06)",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(0,0,0,0.04)",
                      fontWeight: 800,
                      color: "#0f172a",
                    },
                  }}
                />
              </div>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
